import { IConfigSlider } from '../../../classes/custom-range-slider';

export const LOGISTICS_DASHBOARD_SKIN = 'logistics';

export const LOGISTICS_DEFAULT_SEARCH_TERM =
  'manufacturing, retail and e-commerce businesses';

const LOGISTICS_PRESENTATION_PARAMS = new Set([
  'pageNumber',
  'pageSize',
  'resultsMode',
  'savedSearchName',
  'selectedView',
  'sortDirectionIsAscending',
  'sortKey',
]);

export interface LogisticsSearchTag {
  param: string;
  value: unknown;
}

/**
 * HttpParams keeps an update entry for every dashboard field, including empty
 * values and result presentation controls. Only genuine prospect criteria
 * should contribute to the logistics workspace's active-filter summary.
 */
export function isActiveLogisticsFilterTag(
  tag: LogisticsSearchTag | null | undefined
): boolean {
  if (!tag || LOGISTICS_PRESENTATION_PARAMS.has(tag.param)) {
    return false;
  }

  if (Array.isArray(tag.value)) {
    return tag.value.length > 0;
  }

  return ![
    null,
    undefined,
    '',
    false,
    'false',
    'null',
    'undefined',
    '[]',
    '{}',
  ].includes(tag.value as never);
}

export function countActiveLogisticsFilters(
  tags: LogisticsSearchTag[] | null | undefined
): number {
  return (tags || []).filter(isActiveLogisticsFilterTag).length;
}

/**
 * Labels are the backend-generated slider labels from
 * web_app.ui_data.ui_components.slider_config_as_json(). Keeping these values
 * here makes the focused UI reuse the same search parameters as the full
 * dashboard instead of introducing a second logistics-specific API contract.
 */
export const LOGISTICS_FINANCIAL_SLIDER_LABELS = [
  'Revenue',
  'Revenue Growth Rate',
  'Profit After Tax',
  'Inventory',
  'Raw Materials And Consumable',
];

export const LOGISTICS_ASSET_SLIDER_LABELS = [
  'VehicleFleetSize',
  'VehicleFleetSizeAuthorised',
  'TrailerFleetSize',
  'TrailerFleetSizeAuthorised',
  'VehiclesTangibleAssets',
];

export const LOGISTICS_WEB_TECHNOLOGY_CATEGORIES = [
  'Cart abandonment',
  'Cross border ecommerce',
  'Ecommerce',
  'Fulfilment',
  'Shipping carriers',
];

export const LOGISTICS_FUNCTIONAL_DEPARTMENTS = [
  'Distribution',
  'Distribution Staff',
  'Drivers',
  'Drivers and Warehouse Employee',
  'Factory Staff',
  'Logistics',
  'Number Of Distribution Staff',
  'Warehouse',
  'Warehouse and Shipping',
  'Warehouse Distribution',
];

export interface LogisticsNewsEventOption {
  label: string;
  categoryNames: string[];
}

export interface LogisticsNewsEventGroup {
  label: string;
  description: string;
  options: LogisticsNewsEventOption[];
}

/**
 * A deliberately small, logistics-relevant view over the existing news event
 * taxonomy. "New Products/Services" and "Launch of New Product or Service"
 * are presented as one choice because they describe the same user intent, but
 * both backend-owned values are still sent so neither classifier is missed.
 */
export const LOGISTICS_NEWS_EVENT_GROUPS: LogisticsNewsEventGroup[] = [
  {
    label: 'Growth signals',
    description: 'Expansion and scale that can create new logistics demand',
    options: [
      {
        label: 'Product or service launch',
        categoryNames: [
          'New Products/Services',
          'Launch of New Product or Service',
        ],
      },
      { label: 'Geographic expansion', categoryNames: ['Geographic Expansion'] },
      { label: 'Acquisition or merger', categoryNames: ['Acquisition or Merger'] },
      {
        label: 'Major investment or divestment',
        categoryNames: ['Major Investment or Divestment'],
      },
      {
        label: 'Significant revenue growth',
        categoryNames: ['Significant Increase in Revenue'],
      },
    ],
  },
  {
    label: 'Operational triggers',
    description: 'Events that can create an immediate supply-chain requirement',
    options: [
      { label: 'Disruption of operations', categoryNames: ['Disruption of Operations'] },
      { label: 'Product recall', categoryNames: ['Product Recall'] },
    ],
  },
];

export const LOGISTICS_NEWS_EVENT_CATEGORIES = LOGISTICS_NEWS_EVENT_GROUPS.flatMap(
  group => group.options.flatMap(option => option.categoryNames)
);

export const LOGISTICS_MAGIC_INSIGHT_MODULE_NAMES = [
  'Magic Insights - Logistics',
  'Magic Insights - Freight',
] as const;

export interface LogisticsMagicInsightQuestionSummary {
  id: number;
  title: string;
  filterCount: number;
  /**
   * Stable schema keys used to recognise the question at runtime. Question
   * primary keys differ between production and development imports, whereas
   * these field names form part of the search API contract.
   */
  filterFieldNames?: string[];
}

/**
 * Presentation metadata derived from the supplied production template export.
 * Runtime filter definitions still come directly from get_aira_filter_config/
 * and retain the backend-owned field names, types, options and linked fields.
 */
export const LOGISTICS_MAGIC_INSIGHT_QUESTIONS: LogisticsMagicInsightQuestionSummary[] = [
  {
    id: 200,
    title: 'Providers and returns',
    filterCount: 2,
    filterFieldNames: ['returns_policy', 'logistics_providers__name'],
  },
  { id: 201, title: 'Parcel profile', filterCount: 0 },
  { id: 202, title: 'Decision makers', filterCount: 0 },
  {
    id: 203,
    title: 'Monthly parcel volumes',
    filterCount: 2,
    filterFieldNames: [
      'monthly_parcels_or_shipments_sent',
      'monthly_parcels_or_shipments_received',
    ],
  },
  {
    id: 204,
    title: 'International shipment flows',
    filterCount: 4,
    filterFieldNames: [
      'geographical_distribution_of_parcels_or_shipments_sent_from_company__percentage',
      'geographical_distribution_of_parcels_or_shipments_sent_from_company__country_code',
      'geographical_distribution_of_parcels_or_shipments_received_by_company__percentage',
      'geographical_distribution_of_parcels_or_shipments_received_by_company__country_code',
    ],
  },
  { id: 205, title: 'Initiatives and challenges', filterCount: 0 },
  { id: 206, title: 'Logistics operation', filterCount: 0 },
  {
    id: 207,
    title: 'Credit worthiness',
    filterCount: 1,
    filterFieldNames: ['estimated_credit_worthiness_score'],
  },
  {
    id: 208,
    title: 'Customer model and marketplaces',
    filterCount: 2,
    filterFieldNames: ['b2c_ratio', 'online_marketplaces'],
  },
  {
    id: 282,
    title: 'Annual pallet volumes',
    filterCount: 2,
    filterFieldNames: [
      'annual_inbound_pallet_count',
      'annual_outbound_pallet_count',
    ],
  },
  {
    id: 283,
    title: 'Seasonal demand',
    filterCount: 2,
    filterFieldNames: ['seasonality_score', 'seasonal_products_sold'],
  },
  { id: 284, title: 'Logistics technology', filterCount: 0 },
  {
    id: 285,
    title: 'Annual container volumes',
    filterCount: 2,
    filterFieldNames: [
      'annual_inbound_full_containers_count',
      'annual_outbound_full_containers_count',
    ],
  },
  {
    id: 287,
    title: 'Shipment handling',
    filterCount: 2,
    filterFieldNames: [
      'shipment_categories_sent__category_name',
      'shipment_categories_received__category_name',
    ],
  },
  {
    id: 289,
    title: 'Warehouse network',
    filterCount: 3,
    filterFieldNames: [
      'warehouse_details__region',
      'warehouse_details__country_code',
      'number_of_verified_warehouses_used',
    ],
  },
  {
    id: 290,
    title: 'Transport mix',
    filterCount: 4,
    filterFieldNames: [
      'inbound_transport_modes__percentage',
      'inbound_transport_modes__transport_mode',
      'outbound_transport_modes__percentage',
      'outbound_transport_modes__transport_mode',
    ],
  },
];

export interface LogisticsMagicInsightSectionDefinition {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  icon: string;
  questionIds: number[];
}

export interface LogisticsMagicInsightFilterGroup {
  moduleId: number | string;
  moduleName: string;
  questionId: number | string;
  questionReference: number;
  questionTitle: string;
  groupKey: string;
  filters: Record<string, any>[];
  hasFilterAccess: boolean;
}

export interface LogisticsMagicInsightSection
  extends LogisticsMagicInsightSectionDefinition {
  groups: LogisticsMagicInsightFilterGroup[];
  filterCount: number;
  expectedFilterCount: number;
}

export const LOGISTICS_MAGIC_INSIGHT_SECTIONS: LogisticsMagicInsightSectionDefinition[] = [
  {
    id: 'general',
    eyebrow: 'QUALIFY',
    title: 'General logistics',
    description: 'Providers, commercial fit and customer model',
    icon: 'fa-route',
    questionIds: [200, 207, 208],
  },
  {
    id: 'parcel',
    eyebrow: 'PARCEL',
    title: 'Parcel operations',
    description: 'Monthly outbound and inbound parcel demand',
    icon: 'fa-box',
    questionIds: [203],
  },
  {
    id: 'international',
    eyebrow: 'CROSS-BORDER',
    title: 'International trade',
    description: 'AI-estimated origin and destination countries and their share of shipments',
    icon: 'fa-globe-europe',
    questionIds: [204],
  },
  {
    id: 'freight',
    eyebrow: 'FREIGHT',
    title: 'Freight profile',
    description: 'Pallets, containers, handling and transport modes',
    icon: 'fa-truck-container',
    questionIds: [282, 283, 285, 287, 290],
  },
  {
    id: 'warehousing',
    eyebrow: '3PL',
    title: 'Warehousing network',
    description: 'Verified sites, regions and countries',
    icon: 'fa-warehouse',
    questionIds: [289],
  },
];

export const LOGISTICS_MAGIC_INSIGHT_FILTER_COUNT =
  LOGISTICS_MAGIC_INSIGHT_QUESTIONS.reduce(
    (total, question) => total + question.filterCount,
    0
  );

export function buildLogisticsMagicInsightSections(
  modules: Record<string, any>[] | null | undefined
): LogisticsMagicInsightSection[] {
  const allowedModuleNames = new Set<string>(
    LOGISTICS_MAGIC_INSIGHT_MODULE_NAMES
  );
  const relevantModules = (modules || []).filter(module =>
    allowedModuleNames.has(module.sectionName)
  );

  return LOGISTICS_MAGIC_INSIGHT_SECTIONS.map(section => {
    const groups: LogisticsMagicInsightFilterGroup[] = [];

    relevantModules.forEach(module => {
      (module.questions || [])
        .forEach(question => {
          const filters = Object.values(question.filters || {}).flat() as
            Record<string, any>[];
          if (!filters.length) {
            return;
          }

          const fieldNames = filters
            .map(filter => filter.fieldName)
            .filter((fieldName): fieldName is string => Boolean(fieldName))
            .sort();
          const questionSummary = LOGISTICS_MAGIC_INSIGHT_QUESTIONS.find(
            summary => {
              const expectedFields = [...(summary.filterFieldNames || [])].sort();
              return (
                expectedFields.length > 0 &&
                expectedFields.every(fieldName => fieldNames.includes(fieldName))
              );
            }
          );
          if (!questionSummary || !section.questionIds.includes(questionSummary.id)) {
            return;
          }

          groups.push({
            moduleId: module.id,
            moduleName: module.sectionName,
            questionId: question.id,
            questionReference: questionSummary.id,
            questionTitle: questionSummary.title,
            groupKey: `question-${question.id}`,
            filters,
            hasFilterAccess: Boolean(module.hasFilterAccess),
          });
        });
    });

    const expectedFilterCount = LOGISTICS_MAGIC_INSIGHT_QUESTIONS.filter(
      question => section.questionIds.includes(question.id)
    ).reduce((total, question) => total + question.filterCount, 0);

    return {
      ...section,
      groups,
      filterCount: groups.reduce(
        (total, group) => total + group.filters.length,
        0
      ),
      expectedFilterCount,
    };
  });
}

export function selectOptionsByValue<T>(
  options: T[] | null | undefined,
  allowedValues: string[],
  valueSelector: (option: T) => string
): T[] {
  if (!options?.length) {
    return [];
  }

  const allowed = new Set(allowedValues);
  return options.filter(option => allowed.has(valueSelector(option)));
}

export function selectSliderConfigs(
  sliderGroups: Record<string, IConfigSlider[]>,
  labels: string[]
): IConfigSlider[] {
  const sliders = Object.values(sliderGroups || {}).flat();
  return labels
    .map(label => sliders.find(slider => slider.label === label))
    .filter((slider): slider is IConfigSlider => Boolean(slider));
}
